import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Card,
    Table,
    Switch,
    Input,
    Flex,
    Text,
    Dialog,
    Badge,
    IconButton,
    Stack,
} from '@chakra-ui/react';
import { HiPlus, HiTrash, HiRefresh } from 'react-icons/hi';
import axios from '../api';
import { toaster } from './ui/toaster.jsx';
import { Field } from './ui/field.jsx';
import PageHeader from './PageHeader';
import FilterBar from './FilterBar';

const WatchList = () => {
    const [symbols, setSymbols] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [savingId, setSavingId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showActiveOnly, setShowActiveOnly] = useState(false);
    const [formData, setFormData] = useState({
        symbol: '',
        is_active: true,
        stock_stop_loss_percent: '0.3',
        quantity: 0,
    });
    const [formErrors, setFormErrors] = useState({});

    const fetchSymbols = async () => {
        try {
            const response = await axios.get('/watchlist');
            const items = response.data;
            setSymbols(items);
            setDrafts((prev) => {
                const next = {};
                items.forEach((symbol) => {
                    const existing = prev[symbol.id];
                    next[symbol.id] = {
                        stopLoss: existing?.stopLoss ?? (symbol.stock_stop_loss_percent ?? ''),
                        quantity: existing?.quantity ?? (symbol.quantity ?? 0),
                    };
                });
                return next;
            });
        } catch (error) {
            toaster.create({
                title: '获取数据失败',
                description: error.message,
                type: 'error',
            });
        }
    };

    useEffect(() => {
        fetchSymbols();
    }, []);

    const filteredSymbols = useMemo(() => {
        const query = searchQuery.trim().toUpperCase();
        return symbols.filter((symbol) => {
            const matchesQuery = !query || symbol.symbol?.toUpperCase().includes(query);
            const matchesActive = !showActiveOnly || symbol.is_active;
            return matchesQuery && matchesActive;
        });
    }, [symbols, searchQuery, showActiveOnly]);

    const validateForm = () => {
        const errors = {};
        if (!formData.symbol.trim()) {
            errors.symbol = '标的代码不能为空';
        }
        const stopLossPercent = parseFloat(formData.stock_stop_loss_percent);
        if (isNaN(stopLossPercent) || stopLossPercent <= 0 || stopLossPercent > 10) {
            errors.stock_stop_loss_percent = '止损百分比必须在 0-10 之间';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            await axios.post('/watchlist', formData);
            toaster.create({
                title: '添加成功',
                type: 'success',
            });
            setIsModalOpen(false);
            setFormData({ symbol: '', is_active: true, stock_stop_loss_percent: '0.3', quantity: 0 });
            fetchSymbols();
        } catch (error) {
            toaster.create({
                title: '添加失败',
                description: error.response?.data?.detail || error.message,
                type: 'error',
            });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('确定要删除这个标的吗？')) return;

        try {
            await axios.delete(`/watchlist/${id}`);
            toaster.create({
                title: '删除成功',
                type: 'success',
            });
            fetchSymbols();
        } catch (error) {
            toaster.create({
                title: '删除失败',
                description: error.message,
                type: 'error',
            });
        }
    };

    const handleStatusChange = async (id, field, currentValue) => {
        try {
            const symbol = symbols.find((item) => item.id === id);
            await axios.put(`/watchlist/${id}`, {
                ...symbol,
                [field]: !currentValue,
            });
            fetchSymbols();
        } catch (error) {
            toaster.create({
                title: '更新状态失败',
                description: error.message,
                type: 'error',
            });
        }
    };

    const handleDraftChange = (id, field, value) => {
        setDrafts((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                [field]: value,
            },
        }));
    };

    const normalizeStopLoss = (value) => {
        if (value === null || value === undefined) return '';
        return value === '' ? '' : String(value);
    };

    const isRowDirty = (symbol) => {
        const draft = drafts[symbol.id];
        if (!draft) return false;

        const currentStopLoss = normalizeStopLoss(symbol.stock_stop_loss_percent ?? '');
        const draftStopLoss = normalizeStopLoss(draft.stopLoss);
        const stopLossChanged = currentStopLoss !== draftStopLoss;

        const currentQuantity = Number(symbol.quantity || 0);
        const draftQuantity = Number(draft.quantity || 0);
        const quantityChanged = currentQuantity !== draftQuantity;

        return stopLossChanged || quantityChanged;
    };

    const handleResetDraft = (symbol) => {
        setDrafts((prev) => ({
            ...prev,
            [symbol.id]: {
                stopLoss: symbol.stock_stop_loss_percent ?? '',
                quantity: symbol.quantity ?? 0,
            },
        }));
    };

    const handleInlineSave = async (symbol) => {
        const draft = drafts[symbol.id];
        if (!draft) return;

        const stopLossValue = normalizeStopLoss(draft.stopLoss);
        let stopLossPayload = null;
        if (stopLossValue !== '') {
            const parsed = parseFloat(stopLossValue);
            if (isNaN(parsed) || parsed <= 0 || parsed > 10) {
                toaster.create({
                    title: '止损百分比无效',
                    description: '止损百分比必须在 0-10 之间',
                    type: 'error',
                });
                return;
            }
            stopLossPayload = parsed.toString();
        }

        const payload = {
            ...symbol,
            quantity: Number(draft.quantity || 0),
            stock_stop_loss_percent: stopLossPayload,
        };

        try {
            setSavingId(symbol.id);
            await axios.put(`/watchlist/${symbol.id}`, payload);
            toaster.create({
                title: '更新标的设置成功',
                type: 'success',
            });
            fetchSymbols();
        } catch (error) {
            toaster.create({
                title: '更新标的设置失败',
                description: error.response?.data?.detail || error.message,
                type: 'error',
            });
        } finally {
            setSavingId(null);
        }
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('zh-CN', {
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Box>
            <PageHeader
                title="监控标的"
                description="实时监控交易标的，管理止损策略与仓位控制"
                actions={
                    <Button onClick={() => setIsModalOpen(true)} colorPalette="blue" variant="solid">
                        <HiPlus /> 新增标的
                    </Button>
                }
            />

            <FilterBar
                left={
                    <>
                        <Input
                            placeholder="搜索代码..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            maxW="200px"
                            variant="subtle"
                        />
                        <Flex align="center" gap={2}>
                            <Switch.Root
                                size="sm"
                                checked={showActiveOnly}
                                onCheckedChange={({ checked }) => setShowActiveOnly(checked)}
                                colorPalette="blue"
                            >
                                <Switch.HiddenInput />
                                <Switch.Control>
                                    <Switch.Thumb />
                                </Switch.Control>
                            </Switch.Root>
                            <Text fontSize="sm" color="text.subtle">仅显示启用</Text>
                        </Flex>
                    </>
                }
                right={<Badge variant="outline" colorPalette="gray">共 {filteredSymbols.length} 个标的</Badge>}
                summary="支持行内编辑止损与数量，修改后点击保存生效"
            />

            <Card.Root variant="elevated">
                <Card.Body p={0}>
                    <Table.Root size="md" interactive striped>
                        <Table.Header bg="bg.subtle">
                            <Table.Row>
                                <Table.ColumnHeader color="text.muted" fontWeight="600">代码</Table.ColumnHeader>
                                <Table.ColumnHeader color="text.muted" fontWeight="600">更新时间</Table.ColumnHeader>
                                <Table.ColumnHeader color="text.muted" fontWeight="600">监控状态</Table.ColumnHeader>
                                <Table.ColumnHeader color="text.muted" fontWeight="600" textAlign="right">止损 (%)</Table.ColumnHeader>
                                <Table.ColumnHeader color="text.muted" fontWeight="600" textAlign="right">下单数量</Table.ColumnHeader>
                                <Table.ColumnHeader color="text.muted" fontWeight="600" textAlign="right">操作</Table.ColumnHeader>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {filteredSymbols.map((symbol) => {
                                const draft = drafts[symbol.id] || {
                                    stopLoss: symbol.stock_stop_loss_percent ?? '',
                                    quantity: symbol.quantity ?? 0,
                                };
                                const isDirty = isRowDirty(symbol);

                                return (
                                <Table.Row key={symbol.id}>
                                    <Table.Cell fontWeight="bold" fontFamily="mono" color="brand.600">
                                        {symbol.symbol}
                                    </Table.Cell>
                                    <Table.Cell color="text.muted" fontSize="sm">
                                            {formatDateTime(symbol.last_updated)}
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Switch.Root
                                                size="sm"
                                                checked={symbol.is_active}
                                                onCheckedChange={() =>
                                                    handleStatusChange(symbol.id, 'is_active', symbol.is_active)
                                                }
                                                colorPalette="green"
                                            >
                                                <Switch.HiddenInput />
                                                <Switch.Control>
                                                    <Switch.Thumb />
                                                </Switch.Control>
                                            </Switch.Root>
                                        </Table.Cell>
                                        <Table.Cell textAlign="right">
                                            <Input
                                                size="xs"
                                                w="80px"
                                                textAlign="right"
                                                type="number"
                                                step="0.1"
                                                min="0"
                                                max="10"
                                                value={draft.stopLoss}
                                                onChange={(e) =>
                                                    handleDraftChange(symbol.id, 'stopLoss', e.target.value)
                                                }
                                                variant="subtle"
                                                fontFamily="mono"
                                            />
                                        </Table.Cell>
                                        <Table.Cell textAlign="right">
                                            <Input
                                                size="xs"
                                                w="80px"
                                                textAlign="right"
                                                type="number"
                                                min="0"
                                                value={draft.quantity}
                                                onChange={(e) =>
                                                    handleDraftChange(
                                                        symbol.id,
                                                        'quantity',
                                                        parseInt(e.target.value, 10) || 0
                                                    )
                                                }
                                                variant="subtle"
                                                fontFamily="mono"
                                            />
                                        </Table.Cell>
                                        <Table.Cell textAlign="right">
                                            <Flex justify="flex-end" gap={2}>
                                                {isDirty && (
                                                    <>
                                                        <Button
                                                            size="xs"
                                                            variant="outline"
                                                            onClick={() => handleResetDraft(symbol)}
                                                            disabled={savingId === symbol.id}
                                                        >
                                                            重置
                                                        </Button>
                                                        <Button
                                                            size="xs"
                                                            onClick={() => handleInlineSave(symbol)}
                                                            disabled={savingId === symbol.id}
                                                            colorPalette="blue"
                                                        >
                                                            保存
                                                        </Button>
                                                    </>
                                                )}
                                                <IconButton
                                                    size="xs"
                                                    variant="ghost"
                                                    colorPalette="red"
                                                    aria-label="删除"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(symbol.id);
                                                    }}
                                                >
                                                    <HiTrash />
                                                </IconButton>
                                            </Flex>
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                    </Table.Root>
                </Card.Body>
            </Card.Root>

            <Dialog.Root open={isModalOpen} onOpenChange={() => setIsModalOpen(false)}>
                <Dialog.Backdrop />
                <Dialog.Content>
                    <Dialog.Header>
                        <Dialog.Title>添加新标的</Dialog.Title>
                    </Dialog.Header>
                    <Dialog.CloseTrigger />
                    <Dialog.Body>
                        <Stack gap={4}>
                            <Field
                                label="标的代码"
                                required
                                invalid={!!formErrors.symbol}
                                errorText={formErrors.symbol}
                            >
                                <Input
                                    placeholder="例如：AAPL.US"
                                    value={formData.symbol}
                                    onChange={(e) =>
                                        setFormData({ ...formData, symbol: e.target.value.toUpperCase() })
                                    }
                                    fontFamily="mono"
                                />
                            </Field>

                            <Flex align="center" justify="space-between">
                                <Text fontWeight="medium">立即启用</Text>
                                <Switch.Root
                                    checked={formData.is_active}
                                    onCheckedChange={({ checked }) =>
                                        setFormData({ ...formData, is_active: checked })
                                    }
                                    colorPalette="green"
                                >
                                    <Switch.HiddenInput />
                                    <Switch.Control>
                                        <Switch.Thumb />
                                    </Switch.Control>
                                </Switch.Root>
                            </Flex>

                            <Field
                                label="止损百分比 (%)"
                                invalid={!!formErrors.stock_stop_loss_percent}
                                errorText={formErrors.stock_stop_loss_percent}
                            >
                                <Input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="10"
                                    value={formData.stock_stop_loss_percent}
                                    onChange={(e) =>
                                        setFormData({ ...formData, stock_stop_loss_percent: e.target.value })
                                    }
                                    placeholder="0-10"
                                    fontFamily="mono"
                                />
                            </Field>
                            <Field
                                label="下单数量"
                                helperText="0 表示自动计算"
                                error={formErrors.quantity}
                            >
                                <Input
                                    type="number"
                                    min="0"
                                    value={formData.quantity}
                                    onChange={(e) =>
                                        setFormData({ ...formData, quantity: parseInt(e.target.value, 10) || 0 })
                                    }
                                    fontFamily="mono"
                                />
                            </Field>
                        </Stack>
                    </Dialog.Body>

                    <Dialog.Footer>
                        <Button variant="ghost" mr={3} onClick={() => setIsModalOpen(false)}>
                            取消
                        </Button>
                        <Button onClick={handleSubmit} colorPalette="blue">添加</Button>
                    </Dialog.Footer>
                </Dialog.Content>
            </Dialog.Root>
        </Box>
    );
};

export default WatchList;
